import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eggProductionService } from '../services/apiServices';
import { EggProduction, PaginatedResponse, Stats } from '../types';

// Get all egg productions
export const useEggProductions = (params?: any) => {
  return useQuery<PaginatedResponse<EggProduction>>({
    queryKey: ['eggProductions', params],
    queryFn: () => eggProductionService.getAll(params),
  });
};

// Get single egg production
export const useEggProduction = (id: number) => {
  return useQuery<EggProduction>({
    queryKey: ['eggProduction', id],
    queryFn: () => eggProductionService.getById(id),
    enabled: !!id,
  });
};

// Get egg production stats
export const useEggProductionStats = () => {
  return useQuery<Stats>({
    queryKey: ['eggProductionStats'],
    queryFn: () => eggProductionService.getStats(),
  });
};

// Get egg production trends
export const useEggProductionTrends = (params?: any) => {
  return useQuery({
    queryKey: ['eggProductionTrends', params],
    queryFn: () => eggProductionService.getTrends(params),
  });
};

// Get top performing birds
export const useTopPerformance = () => {
  return useQuery({
    queryKey: ['eggProductionTopPerformance'],
    queryFn: () => eggProductionService.getTopPerformance(),
  });
};

// Create egg production mutation
export const useCreateEggProduction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<EggProduction>) => eggProductionService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eggProductions'] });
      queryClient.invalidateQueries({ queryKey: ['eggProductionStats'] });
      queryClient.invalidateQueries({ queryKey: ['eggProductionTrends'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardOverview'] });
    },
  });
};

// Update egg production mutation
export const useUpdateEggProduction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<EggProduction> }) =>
      eggProductionService.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['eggProductions'] });
      queryClient.invalidateQueries({ queryKey: ['eggProduction', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['eggProductionStats'] });
      queryClient.invalidateQueries({ queryKey: ['eggProductionTrends'] });
    },
  });
};

// Delete egg production mutation
export const useDeleteEggProduction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => eggProductionService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eggProductions'] });
      queryClient.invalidateQueries({ queryKey: ['eggProductionStats'] });
      queryClient.invalidateQueries({ queryKey: ['eggProductionTrends'] });
    },
  });
};

// Get bird day-to-day production
export const useBirdDayToDayProduction = (
  birdId: string,
  dateFrom?: string,
  dateTo?: string,
  page?: number,
  perPage?: number
) => {
  return useQuery({
    queryKey: ['birdDayToDayProduction', birdId, dateFrom, dateTo, page, perPage],
    queryFn: async () => {
      if (!birdId) return null;
      const params: any = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (page) params.page = page;
      if (perPage) params.per_page = perPage;

      const response = await eggProductionService.getBirdDayToDay(Number(birdId), params);
      return response;
    },
    enabled: !!birdId,
  });
};

// Get bird production summary
export const useBirdProductionSummary = (birdId: string, dateFrom?: string, dateTo?: string) => {
  return useQuery({
    queryKey: ['birdProductionSummary', birdId, dateFrom, dateTo],
    queryFn: async () => {
      if (!birdId) return null;
      const params: any = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await eggProductionService.getBirdDayToDaySummary(Number(birdId), params);
      return response;
    },
    enabled: !!birdId,
  });
};
